import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Switch } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { CustomIconButton, DropDown, IconComponent, icon } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import styles from "./ManageColumnsButton.module.css";
import { checkValidity } from "../../../../navigation";
import { setDocCenterTableColumnOrder } from "../../slice/document-center-slice";

const COLUMN_LABEL_KEYS = {
	FILENAME: "document-center.table.column.name",
	VERSION: "document-center.table.column.version",
	UPDATED: "document-center.table.column.updated",
	PAGES: "document-center.table.column.pages",
	REQUIREMENT: "document-center.table.column.requirements",
	QUALITY: "document-center.table.column.quality",
};

export default function ManageColumnsButton() {
	const columnOrder = useSelector(({ documentCenter }) => documentCenter.tableColumnOrder);
	const dispatch = useDispatch();
	const anchorRef = useRef();
	const [open, setOpen] = useState(false);
	const [dragPortal] = useState(() => document.createElement("div"));

	useEffect(() => {
		document.body.appendChild(dragPortal);
		return () => document.body.removeChild(dragPortal);
	}, [dragPortal]);

	const handleDragEnd = (result) => {
		if (!result.destination || result.destination.index === result.source.index) {
			return;
		}
		const nextOrder = [...columnOrder];
		const [moved] = nextOrder.splice(result.source.index, 1);
		nextOrder.splice(result.destination.index, 0, moved);
		dispatch(setDocCenterTableColumnOrder(nextOrder));
	};
	const handleToggleVisibility = (key) => {
		dispatch(
			setDocCenterTableColumnOrder(
				columnOrder.map((colOrder) => {
					if (colOrder.key === key) {
						return { ...colOrder, visibility: !colOrder.visibility };
					}
					return colOrder;
				})
			)
		);
	};
	return (
		<>
			<CustomIconButton
				ref={anchorRef}
				icon={icon.faColumns}
				size="sm"
				tooltip={translate("document-center.table.manage-columns.button-tooltip")}
				variant="outlined"
				onClick={() => setOpen((prev) => !prev)}
			/>
			<DropDown ref={anchorRef} open={open} position="bottom-end" onClose={() => setOpen(false)}>
				<div className={styles.panel}>
					<p className={styles.helperText}>{translate("document-center.table.manage-columns.helper-text")}</p>
					<div className={styles.row} data-locked="true">
						<IconComponent className={styles.gripIcon} icon={icon.faGripLines} />
						<span className={styles.label}>{translate(COLUMN_LABEL_KEYS.FILENAME)}</span>
						<Switch checked disabled color="primary" size="small" />
					</div>
					<DragDropContext onDragEnd={handleDragEnd}>
						<Droppable droppableId="manage-columns">
							{(dropProvided) => (
								<div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
									{columnOrder
										.filter((colOrder) => checkValidity(colOrder))
										.map((col, index) => (
											<Draggable key={col.key} draggableId={col.key} index={index}>
												{(provided, snapshot) => {
													const row = (
														<div
															ref={provided.innerRef}
															className={styles.row}
															{...provided.draggableProps}
														>
															<span
																className={styles.gripIcon}
																{...provided.dragHandleProps}
															>
																<IconComponent icon={icon.faGripLines} />
															</span>
															<span className={styles.label}>
																{translate(col.translation)}
															</span>
															<Switch
																checked={col.visibility !== false}
																color="primary"
																size="small"
																onChange={() => handleToggleVisibility(col.key)}
															/>
														</div>
													);
													return snapshot.isDragging ? createPortal(row, dragPortal) : row;
												}}
											</Draggable>
										))}
									{dropProvided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>
			</DropDown>
		</>
	);
}
